import React, { useState, useEffect, useMemo } from 'react';
import { Table, TableBody, TableHead, TableRow } from '@aws-amplify/ui-react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import ReactStars from 'react-rating-stars-component';

import { TypographyText, Row, Col } from 'components/UI/atoms';
import { TooltipCard } from 'components/UI/molecules';
import {
  codeMap,
  insuranceSurveyCodes,
  omitReqSurveyCdByIsrcSurveyCd,
  conditionalSurveyCodes,
} from 'utils/code';
import { TableCell } from 'components/UI/atoms/table/TableCell';
import { useStores } from 'stores/Context';
import { getSummarySurveyData, getConditionalCategories } from 'utils/util';
import { treatmentModeMap } from 'constant';

import FeedbackResultTd from './FeedbackResultTd';
import ScaleTd from './ScaleTd';
import CategoryTd from './CategoryTd';
import SymptomDescTd from './SymptomDescTd';
import SymptomHighlightTd from './SymptomHighlightTd';
import FeedbackCheckTd from './FeedbackCheckTd';

// 피드백 데이터 파싱 유틸리티
const parseFeedbackData = (survey: any) => {
  let feedbackResult = [];
  if (!_.isEmpty(survey?.feedbackResult) && survey?.feedbackResult !== 'none') {
    feedbackResult = JSON.parse(survey?.feedbackResult);
  }

  let surveyCodeArrForfeedback = [];
  if (!_.isEmpty(survey?.feedbacks) && survey?.feedbacks !== 'none') {
    surveyCodeArrForfeedback = JSON.parse(survey?.feedbacks)?.map((e) => e.code);
  }

  const feedbackResultObject = feedbackResult.reduce((a, c) => ({ ...a, [c.code]: c.value }), {}) ?? [];

  return { feedbackResult, surveyCodeArrForfeedback, feedbackResultObject };
};

// 테이블 헤더 생성 헬퍼
const createTableHeader = (
  label: string,
  style: React.CSSProperties = {},
  rowStyle: React.CSSProperties = {},
  children?: React.ReactNode,
) => {
  return (
    <TableCell as="th" style={style}>
      <Row style={{ display: 'flex', alignItems: 'center', ...rowStyle }}>
        {children || (
          <TypographyText fontSize="0.8rem" lineHeight="1.2em" fontWeight={700} whiteSpace="pre">
            {label}
          </TypographyText>
        )}
      </Row>
    </TableCell>
  );
};

// "증상 없음" 행 렌더링 함수
const renderNoResponseRow = (
  symptom: any,
  data: any,
  borderStyle: any,
  mode: string,
  thListLength: number,
) => {
  const categoryCell = (
    <TableCell style={{ borderLeft: 'none', borderRight: 'none', paddingLeft: '0' }}>
      <TypographyText yjTypeTypographyText="symptom-table-body" fontWeight={700} letterSpacing={'-0.02rem'}>
        [{data?.label}]
      </TypographyText>
    </TableCell>
  );

  const emptyHighlightCell = (
    <TableCell
      textAlign={'center'}
      style={{ borderLeft: 'none', borderRight: 'none', paddingLeft: '0', paddingRight: '0.6rem' }}
    />
  );

  const noSymptomCell = (
    <TableCell style={{ borderLeft: 'none', borderRight: 'none', paddingRight: '0', minWidth: '9.6rem' }}>
      <TypographyText yjTypeTypographyText="symptom-table-body" color="#999999">
        증상 없음
      </TypographyText>
    </TableCell>
  );

  const emptyCell = (
    <TableCell style={{ borderLeft: 'none', borderRight: 'none', paddingLeft: 0, paddingRight: 0 }} />
  );

  switch (mode) {
    case 'patientSymptom':
      return (
        <TableRow key={symptom.code} style={borderStyle}>
          {categoryCell}
          {noSymptomCell}
          {emptyCell}
        </TableRow>
      );
    case 'feedbackHistory':
    case 'treatmentSymptom':
      return (
        <TableRow key={symptom.code} style={borderStyle}>
          {categoryCell}
          {emptyHighlightCell}
          {noSymptomCell}
          {emptyCell}
          {emptyCell}
        </TableRow>
      );
    case 'treatmentSymptomReadOnlyForPrecriptionPage':
      return (
        <TableRow key={symptom.code} style={borderStyle}>
          {categoryCell}
          {emptyHighlightCell}
          {noSymptomCell}
          {emptyCell}
        </TableRow>
      );
    default:
      return null;
  }
};

// 일반 증상 행 렌더링 함수
const renderSymptomRow = (
  symptom: any,
  data: any,
  plainStringValue: any,
  desc: any,
  borderStyle: any,
  shouldOmitCategory: boolean,
  isNoResponse: boolean,
  isFirstInCategory: boolean,
  isLastInCategory: boolean,
  isLastSubCategory: boolean,
  mode: string,
  highlight: any,
  setHighlight: any,
  checklist: any,
  setChecklist: any,
  feedbackResult: any,
  feedbackResultObject: any,
  surveyCodeArrForfeedback: any,
  feedbackScheduleFlag: boolean | undefined,
) => {
  const categoryProps = {
    data,
    categoryOmitFlag: shouldOmitCategory,
    isSubItem: !isNoResponse,
    isFirstInCategory: !isNoResponse && isFirstInCategory,
    isLastInCategory: !isNoResponse && isLastInCategory,
    isMiddleInCategory: !isNoResponse && !isFirstInCategory && !isLastInCategory,
    isLastSubCategory,
  };

  switch (mode) {
    case 'patientSymptom':
      return (
        <TableRow key={symptom.code} style={borderStyle}>
          <CategoryTd {...categoryProps} />
          <SymptomDescTd data={data} plainStringValue={plainStringValue} desc={desc} />
          <ScaleTd symptom={symptom} />
        </TableRow>
      );
    case 'feedbackHistory':
      return (
        <TableRow key={symptom.code} style={borderStyle}>
          <CategoryTd {...categoryProps} />
          <SymptomHighlightTd
            symptom={symptom}
            highlight={highlight}
            setHighlight={setHighlight}
            readonlyFlag
          />
          <SymptomDescTd data={data} plainStringValue={plainStringValue} desc={desc} />
          <ScaleTd symptom={symptom} />
          <FeedbackResultTd
            symptom={symptom}
            feedbackResult={feedbackResult}
            feedbackResultObject={feedbackResultObject}
            surveyCodeArrForfeedback={surveyCodeArrForfeedback}
            feedbackScheduleFlag={feedbackScheduleFlag}
          />
        </TableRow>
      );
    case 'treatmentSymptom':
      return (
        <TableRow key={symptom.code} style={borderStyle}>
          <CategoryTd {...categoryProps} />
          <SymptomHighlightTd
            symptom={symptom}
            highlight={highlight}
            setHighlight={setHighlight}
            readonlyFlag={false}
          />
          <SymptomDescTd data={data} plainStringValue={plainStringValue} desc={desc} />
          <ScaleTd symptom={symptom} />
          <FeedbackCheckTd symptom={symptom} checklist={checklist} setChecklist={setChecklist} />
        </TableRow>
      );
    case 'treatmentSymptomReadOnlyForPrecriptionPage':
      return (
        <TableRow key={symptom.code} style={borderStyle}>
          <CategoryTd {...categoryProps} />
          <SymptomHighlightTd
            symptom={symptom}
            highlight={highlight}
            setHighlight={setHighlight}
            readonlyFlag
          />
          <SymptomDescTd data={data} plainStringValue={plainStringValue} desc={desc} />
          <ScaleTd symptom={symptom} />
        </TableRow>
      );
    default:
      return null;
  }
};

const CommonSymptomTable = ({
  survey,
  symptoms = [],
  mode,
  treatment,
  feedbackScheduleFlag,
  patientData,
}: {
  patientData: any;
  survey: any;
  symptoms: any;
  mode:
    | 'patientSymptom' // 환자 관리 - 설문 내역 & 진료실 - 진료 목록 - 증상 보기
    | 'feedbackHistory' // 환자 관리 - 진료 내역 및 피드백 탭
    | 'treatmentSymptom' // 진료실 - 증상 탭
    | 'treatmentSymptomReadOnlyForPrecriptionPage'; // 진료실 - 처방 탭 & 진료 내역
  treatment?: any;
  feedbackScheduleFlag?: boolean;
}) => {
  const { feedbackStore } = useStores();
  const [highlight, setHighlight] = useState({});
  const [checklist, setChecklist] = useState({});
  const [isOpenTooltip1, setIsOpenTooltip1] = useState(false);
  const [isOpenTooltip2, setIsOpenTooltip2] = useState(false);

  // 피드백 데이터 파싱
  const { feedbackResult, surveyCodeArrForfeedback, feedbackResultObject } = parseFeedbackData(survey);

  const insuranceSurveyTypeFlag = useMemo(
    () => treatmentModeMap.has(survey?.insuranceType) && survey?.insuranceType !== 'GENERAL',
    [survey?.insuranceType],
  );

  const sortedSymptoms = useMemo(() => {
    // 응답한 증상을 코드별로 매핑
    const symptomMap = new Map();
    if (_.isArray(symptoms)) {
      symptoms.forEach((symptom) => {
        symptomMap.set(symptom.code, symptom);
      });
    }

    const allSymptoms = [];

    // 1. 먼저 최상위 카테고리들을 확인 (code.length === 1)
    const topLevelCategories = Array.from(codeMap.entries()).filter(([code]) => code.length === 1);

    const conditionalCategories = getConditionalCategories({
      birth: patientData?.birth,
      gender: patientData?.gender,
    });

    topLevelCategories.forEach(([categoryCode, categoryData]) => {
      // 보험적용 급여 설문은 응답이 없으면 완전히 제외
      if (insuranceSurveyCodes.has(categoryCode)) {
        // 해당 카테고리의 응답된 항목만 추가
        symptoms
          .filter((s) => s.code.startsWith(categoryCode))
          .forEach((s) => {
            allSymptoms.push(s);
          });
        return;
      }

      if (
        // 첩건 설문은 특정 설문 카테고리를 응답받지않으므로 생략
        insuranceSurveyTypeFlag &&
        omitReqSurveyCdByIsrcSurveyCd.get(survey?.insuranceType)?.includes(categoryCode)
      ) {
        return;
      } else if (!conditionalCategories.has(categoryCode) && conditionalSurveyCodes.has(categoryCode)) {
        // 나이, 성별에 따라 필터링 되어야하는 설문들 생략
        return;
      }

      // 일반 설문: 하위 응답 확인
      const subResponses = symptoms.filter((s) => s.code.startsWith(categoryCode));

      if (subResponses.length > 0) {
        // 하위 응답이 있으면 응답한 항목만 추가
        subResponses.forEach((s) => allSymptoms.push(s));
      } else {
        // 하위 응답이 없으면 최상위 카테고리만 '증상 없음'으로 추가
        // answers가 있거나 desc가 있으면 유효한 설문 항목으로 간주
        if (categoryData.answers || categoryData.desc) {
          allSymptoms.push({
            code: categoryCode,
            value: null,
            scale: null,
            isTopLevelNoResponse: true, // 최상위 카테고리 증상 없음 플래그
          });
        }
      }
    });

    // 2. 다른 최상위 카테고리에 속하지 않는 독립적인 설문들 처리
    symptoms.forEach((symptom) => {
      // 이미 추가된 항목은 건너뛰기
      if (allSymptoms.some((s) => s.code === symptom.code)) {
        return;
      }
      // 최상위 카테고리가 없는 독립 설문
      if (!topLevelCategories.some(([code]) => symptom.code.startsWith(code))) {
        allSymptoms.push(symptom);
      }
    });

    // 첩건 && 주증상 카테고리가 있으면 해당 항목을 앞으로 정렬
    if (survey.mainSymptomCategory && insuranceSurveyTypeFlag) {
      const classifySymptoms = allSymptoms.reduce(
        (a, c) => {
          if (c.code.startsWith(survey.mainSymptomCategory)) {
            return {
              ...a,
              mainCategory: [...a.mainCategory, c],
            };
          } else {
            return {
              ...a,
              nonMainCategory: [...a.nonMainCategory, c],
            };
          }
        },
        {
          mainCategory: [],
          nonMainCategory: [],
        },
      );
      return [...classifySymptoms.mainCategory, ...classifySymptoms.nonMainCategory];
    } else {
      return allSymptoms;
    }
  }, [insuranceSurveyTypeFlag, survey.mainSymptomCategory, symptoms]);

  const categoryTh = useMemo(
    () =>
      createTableHeader(
        '카테고리',
        { borderLeft: 'none', width: '8.5rem', padding: '0' },
        { padding: '0.5rem 0' },
      ),
    [],
  );

  const symptomDescTh = useMemo(
    () =>
      createTableHeader(
        '증상',
        { padding: '0', textIndent: '0.1rem' },
        { padding: '0.5rem 0 0.5rem 0.5rem' },
      ),
    [],
  );

  const scaleTh = useMemo(
    () =>
      createTableHeader(
        '',
        { padding: '0' },
        {
          justifyContent: 'center',
          textAlign: 'center',
          padding: '0.5rem 0 0.5rem 0.3rem',
        },
        <TypographyText
          fontSize="0.8rem"
          lineHeight="1em"
          fontWeight={700}
          whiteSpace="pre-wrap"
          wordBreak="keep-all"
        >
          {`불편한 정도(1~5)`}
        </TypographyText>,
      ),
    [],
  );

  const symptomHighlightTh = useMemo(() => {
    return (
      <TableCell
        as="th"
        style={{
          padding: '0',
        }}
      >
        <Row
          style={{
            padding: '0.5rem 0',
            display: 'flex',
            alignItems: 'center',
            columnGap: '0.05rem',
            justifyContent: 'center',
            textIndent: '-0.15rem',
          }}
        >
          <TooltipCard
            isOpen={isOpenTooltip1}
            targetComponentId={`common-symptom-table-${mode}`}
            topRem={-3.75}
            leftRem={0}
            description={
              mode === 'treatmentSymptom'
                ? `환자의 주된 호소 증상 선택시, 해당 증상에 가중치가 
부여되어 변증 및 약재 추천에 반영됩니다.`
                : `환자 증상 중 중점적으로 볼 항목입니다. 
선택된 증상에 가중치가 부여되어 약재가 추천됩니다.`
            }
          />
          <TypographyText fontSize="0.8rem" lineHeight="1.2em" fontWeight={700} whiteSpace="pre">
            중요
          </TypographyText>
          <Col
            onMouseOver={() => setIsOpenTooltip1(true)}
            onMouseLeave={() => setIsOpenTooltip1(false)}
            style={{ display: 'flex', cursor: 'pointer', flexShrink: 0 }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/img/question-icon-white.webp`}
              style={{ width: '0.7rem', cursor: 'pointer' }}
              alt="question-icon"
            />
          </Col>
        </Row>
      </TableCell>
    );
  }, [mode, isOpenTooltip1]);

  const feedbackTh = useMemo(() => {
    return (
      <TableCell
        as="th"
        style={{
          borderRight: 'none',
          padding: '0.5rem 0rem 0.5rem 0.3rem',
        }}
      >
        <Row
          style={{ display: 'flex', alignItems: 'center', columnGap: '0.05rem', justifyContent: 'center' }}
        >
          <TooltipCard
            isOpen={isOpenTooltip2}
            targetComponentId={`common-symptom-table-${mode}`}
            topRem={mode === 'treatmentSymptom' ? -4.71 : -3.75}
            rightRem={0}
            description={
              mode === 'treatmentSymptom'
                ? `진료 및 복약 후 선택한 증상의 개선 여부를 환자에게 묻게 됩니다.
피드백을 원하시는 증상을 선택해주세요. (설정하신 기간이 되면
환자에게 카카오톡을 통해 피드백 설문 링크를 발송합니다.)`
                : `선택한 증상의 피드백을 확인하는 카카오톡 메시지를 환자에게 발송합니다.
환자가 메시지에 응답하면 피드백을 확인할 수 있습니다.`
            }
          />
          <TypographyText fontSize="0.8rem" lineHeight="1.2em" fontWeight={700} whiteSpace="pre">
            피드백
          </TypographyText>
          <Col
            onMouseOver={() => setIsOpenTooltip2(true)}
            onMouseLeave={() => setIsOpenTooltip2(false)}
            style={{ display: 'flex', cursor: 'pointer', flexShrink: 0 }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/img/question-icon-white.webp`}
              style={{ width: '0.7rem', cursor: 'pointer' }}
              alt="question-icon"
            />
          </Col>
        </Row>
      </TableCell>
    );
  }, [mode, isOpenTooltip2]);

  const thList = useMemo(() => {
    switch (mode) {
      case 'patientSymptom':
        return [categoryTh, symptomDescTh, scaleTh];
      case 'feedbackHistory':
      case 'treatmentSymptom':
        return [categoryTh, symptomHighlightTh, symptomDescTh, scaleTh, feedbackTh];
      case 'treatmentSymptomReadOnlyForPrecriptionPage':
        return [categoryTh, symptomHighlightTh, symptomDescTh, scaleTh];
      default:
        return [categoryTh, symptomDescTh, scaleTh];
    }
  }, [mode, categoryTh, symptomDescTh, scaleTh, symptomHighlightTh, feedbackTh]);

  useEffect(() => {
    if (
      ['treatmentSymptom', 'feedbackHistory', 'treatmentSymptomReadOnlyForPrecriptionPage'].includes(mode) &&
      !_.isEmpty(treatment)
    ) {
      const symptomCodes = symptoms?.reduce(
        (a, c) => ({
          ...a,
          [c.code]: treatment.surveyCheck?.includes(c.code),
        }),
        {},
      );
      setHighlight(symptomCodes);
    }
  }, [mode, symptoms, treatment]);

  useEffect(() => {
    if (mode === 'treatmentSymptom') {
      const SurveyCodeArr = feedbackStore?.symptoms?.map((e) => e.code);
      const symptomCodes = symptoms?.reduce(
        (a, c) => ({
          ...a,
          [c.code]: _.includes(SurveyCodeArr, c.code),
        }),
        {},
      );
      setChecklist(symptomCodes ?? {});
    }
  }, [mode, feedbackStore, symptoms]);

  return (
    <Row>
      <Row style={{ marginBottom: '0.5rem' }}>
        {mode !== 'patientSymptom' && survey?.feedbackGrade > 0 && (
          <>
            <Row
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '2rem',
                borderTop: '1px solid #e5e5e5',
                borderBottom: '1px solid #f2f2f2',
              }}
            >
              <TypographyText fontSize="0.8rem" lineHeight="1.2em" fontWeight={700}>
                진료만족도
              </TypographyText>
            </Row>
            <Row
              style={{
                display: 'flex',
                columnGap: '1rem',
                alignItems: 'center',
                paddingBlock: '0.7rem',
              }}
            >
              <ReactStars
                activeColor="#FFCE21"
                color="#D9D9D9"
                size={21}
                edit={false}
                value={survey.feedbackGrade}
                classNames="feedback-grade"
              />
              <TypographyText yjTypeTypographyText="sub-title">{survey.feedbackSatisfaction}</TypographyText>
            </Row>
          </>
        )}
        <Row
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '2rem',
            borderTop: '2px solid #e5e5e5',
            borderBottom: '1px solid #e5e5e5',
          }}
        >
          <TypographyText fontSize="0.8rem" lineHeight="1.2em" fontWeight={700}>
            주소증
          </TypographyText>
        </Row>
        <Row
          style={{
            display: 'flex',
            columnGap: '0.4rem',
            alignItems: 'center',
            paddingBlock: '0.75rem',
          }}
        >
          {survey.mainSymptomCategory && codeMap.has(survey.mainSymptomCategory) && (
            <TypographyText yjTypeTypographyText="sub-title" fontWeight={700}>
              {`[${codeMap.get(survey.mainSymptomCategory)?.label}]`}
            </TypographyText>
          )}
          <TypographyText yjTypeTypographyText="sub-title">
            {survey.mainSymptom || (insuranceSurveyTypeFlag ? '' : '주소증 미입력')}
          </TypographyText>
        </Row>
        {survey?.additionalSymptoms && (
          <>
            <Row
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '2rem',
                borderTop: '1px solid #e5e5e5',
                borderBottom: '1px solid #f2f2f2',
              }}
            >
              <TypographyText fontSize="0.8rem" lineHeight="1.2em" fontWeight={700}>
                추가증상
              </TypographyText>
            </Row>
            <Row
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingBlock: '0.75rem',
              }}
            >
              <TypographyText fontSize="1rem" lineHeight="1.2em">
                {survey.additionalSymptoms}
              </TypographyText>
            </Row>
          </>
        )}
      </Row>
      <Row>
        <Col>
          <Table
            id={`common-symptom-table-${mode}`}
            highlightOnHover={false}
            size={undefined}
            variation={undefined}
            style={{
              position: 'relative',
              border: '1px solid #f2f2f2',
              borderInline: 'none',
            }}
          >
            <TableHead>
              <TableRow
                style={{
                  borderTop: '1px solid #e5e5e5',
                  borderBottom: '1px solid #f2f2f2',
                  backgroundColor: '#ffffff',
                }}
              >
                {thList}
              </TableRow>
            </TableHead>
            <TableBody>
              {
                // 복수 응답이 존재하는 것을 제외한 최상위 설문은 보여주지 않는다.
                (() => {
                  const filteredSymptoms = _.filter(
                    sortedSymptoms,
                    (symptom) =>
                      !(
                        symptom?.code?.length === 1 &&
                        !_.isArray(symptom?.value) &&
                        !symptom?.isTopLevelNoResponse
                      ),
                    // eslint-disable-next-line array-callback-return
                  );

                  // 카테고리별로 그룹화
                  const categoryGroups = filteredSymptoms.reduce((groups: any, symptom: any) => {
                    const topLevelCode = symptom.code ? symptom.code.charAt(0) : '';
                    if (!groups[topLevelCode]) {
                      groups[topLevelCode] = [];
                    }
                    groups[topLevelCode].push(symptom);
                    return groups;
                  }, {});

                  const rows: any[] = [];
                  let rowIndex = 0;

                  Object.entries(categoryGroups).forEach(([categoryCode, symptoms]: [string, any]) => {
                    const categoryData = codeMap.get(categoryCode);
                    const isNoResponse = symptoms.length === 1 && symptoms[0].isTopLevelNoResponse;

                    // 대카테고리 내 고유한 하위 카테고리(label) 리스트 추출
                    const uniqueLabels = Array.from(
                      new Set(
                        symptoms
                          .filter((s: any) => !s.isTopLevelNoResponse)
                          .map((s: any) => {
                            const data = getSummarySurveyData(s).codeData;
                            return data?.label || '';
                          })
                          .filter((label: string) => label !== ''),
                      ),
                    );
                    const lastSubCategoryLabel = uniqueLabels[uniqueLabels.length - 1];

                    // 응답없음이 아닌 경우에만 카테고리 헤더 추가
                    if (!isNoResponse && categoryData) {
                      const headerStyle = {
                        borderTop: rowIndex === 0 ? '1px solid #e8e8e8' : '1px solid #e8e8e8',
                        backgroundColor: '#ffffff',
                      };

                      rows.push(
                        <TableRow key={`header-${categoryCode}`} style={headerStyle}>
                          <TableCell
                            colSpan={thList.length}
                            style={{
                              paddingLeft: '0',
                              paddingBlock: '8px',
                              borderLeft: 'none',
                              borderRight: 'none',
                            }}
                          >
                            <TypographyText fontSize="0.95rem" fontWeight={700} letterSpacing={'-0.02rem'}>
                              [{categoryData.label}]
                            </TypographyText>
                          </TableCell>
                        </TableRow>,
                      );
                      rowIndex++;
                    }

                    // 응답없음인 경우 별도 처리하지 않고 일반 항목으로 진행
                    // 각 증상 항목 처리
                    let prevLabel = '';
                    symptoms.forEach((symptomData: any, index: number) => {
                      const {
                        symptom,
                        plainStringValue,
                        codeData: data,
                        desc,
                      } = getSummarySurveyData(symptomData);

                      const isFirstInCategory = index === 0;
                      const isLastInCategory = index === symptoms.length - 1;
                      const isLastOverall =
                        rowIndex === Object.keys(categoryGroups).length - 1 && isLastInCategory;

                      // 경계선 스타일 결정
                      let borderStyle: any = {};
                      if (!isNoResponse && isFirstInCategory) {
                        // 카테고리 헤더 바로 다음 항목은 점선
                        borderStyle.borderTop = '1px dashed #f5f5f5';
                      } else if (index > 0) {
                        borderStyle.borderTop = '1px dashed #f5f5f5';
                      }

                      if (isLastOverall) {
                        borderStyle.borderBottom = '1px solid #e8e8e8';
                      }

                      // 이전 카테고리 라벨 추적 (중복 제거용)
                      const currentLabel = data?.label || '';
                      const shouldOmitCategory =
                        !symptomData.isTopLevelNoResponse && index > 0 && currentLabel === prevLabel;
                      if (!symptomData.isTopLevelNoResponse && currentLabel) {
                        prevLabel = currentLabel;
                      }

                      // 마지막 하위카테고리의 마지막 행인지 판단
                      const isLastSubCategory = currentLabel === lastSubCategoryLabel;

                      // 최상위 카테고리 증상 없음 처리
                      if (symptomData.isTopLevelNoResponse) {
                        const row = renderNoResponseRow(symptom, data, borderStyle, mode, thList.length);
                        if (row) rows.push(row);
                      } else {
                        // 일반 증상 항목
                        const row = renderSymptomRow(
                          symptom,
                          data,
                          plainStringValue,
                          desc,
                          borderStyle,
                          shouldOmitCategory,
                          isNoResponse,
                          isFirstInCategory,
                          isLastInCategory,
                          isLastSubCategory,
                          mode,
                          highlight,
                          setHighlight,
                          checklist,
                          setChecklist,
                          feedbackResult,
                          feedbackResultObject,
                          surveyCodeArrForfeedback,
                          feedbackScheduleFlag,
                        );
                        if (row) rows.push(row);
                      }
                    });
                    rowIndex++;
                  });

                  return rows;
                })()
              }
            </TableBody>
          </Table>
        </Col>
      </Row>
    </Row>
  );
};

export default observer(CommonSymptomTable);
